/**
 * WP4 — Pflichtfeld-Validierung für das PB-Profil-Formular
 *
 * Definiert welche Felder immer (Kontaktperson wie Pflegeempfänger) und
 * welche nur bei is_pflegeempfaenger=true Pflicht sind. Wird sowohl in
 * PbProfileEdit als auch in FamilyMemberForm genutzt.
 */
import type { PbFormState, SectionPerms } from './sections';
import { isSectionActive, TOGGLE_SECTIONS, type ToggleSectionId } from './sectionToggles';

/** Immer pflichttig — gilt für Kontaktpersonen wie Pflegeempfänger. */
export const REQUIRED_CONTACT: Array<keyof PbFormState> = [
  // Nur Felder, die AUCH in der schlanken Kontakt-Ansicht (FamilyMemberForm)
  // gerendert werden — sonst ließe sich ein Kontakt nie speichern (das Pflichtfeld
  // wäre nicht ausfüllbar). familienstand/land sind nur im vollen Formular sichtbar.
  'profiltyp',      // ProfilHeadSection — immer sichtbarer Select
  'vorname',        // Stammdaten Text — immer
  'nachname',       // Stammdaten Text — immer
  'anrede',         // Stammdaten Select — auch in der schlanken Ansicht
];

/**
 * Stammdaten, die nur beim Pflegeempfänger Pflicht sind — unabhängig vom
 * Schalter der Pflege-Sektion und auch in der schlanken Ansicht, weil die
 * Felder in Sektion 01 immer gerendert werden.
 */
export const REQUIRED_PFLEGE_STAMMDATEN: Array<keyof PbFormState> = [
  'telefon',        // Stammdaten Telefon (Hausanschluss, nicht Mobil)
];

/** Zusätzlich pflichttig wenn is_pflegeempfaenger true ist. */
export const REQUIRED_PFLEGE: Array<keyof PbFormState> = [
  'pflegegrad',       // Pflege-Sektion Select
  'beweglichkeit',    // Pflege-Sektion Select
  'geisteszustand',   // Pflege-Sektion Select
  'schlafverhalten',  // Pflege-Sektion Select
  'grundpflege',      // Pflege-Sektion Select
  'hygiene',          // Pflege-Sektion Select
  'ankleiden',        // Pflege-Sektion Select
  'essen',            // Pflege-Sektion Select
  'urinkontrolle',    // Pflege-Sektion Select
  'stuhlkontrolle',   // Pflege-Sektion Select
  'raucher',          // Pflege-Sektion Select
  'besserung',        // Pflege-Sektion Select
];

/**
 * Zusätzlich pflichttig, wenn Sektion 05 Wunsch-BK aktiv ist: die Kategorie
 * (Badge) steht dort, gehört aber nicht zu den Schalter-Feldern der Sektion
 * (sie entscheidet nicht über „Sektion hat Daten"). „0" = „Kein Status" gilt
 * als nicht gewählt (Ticket „Feld Kategorie soll Pflichtfeld werden").
 */
export const REQUIRED_WUNSCH: Array<keyof PbFormState> = ['badge'];

/** Werte, die bei einem Pflichtfeld als „nicht ausgefüllt" zählen. */
const EMPTY_VALUES: Partial<Record<keyof PbFormState, ReadonlyArray<string>>> = {
  badge: ['0'],
};

/**
 * Felder der schaltbaren Sektionen 02–07, die trotz aktiver Sektion NICHT
 * Pflicht sind:
 * - Ja/Nein-Schalter (ToggleRow): „aus" ist eine gültige Antwort, es gibt
 *   keinen „nicht ausgefüllt"-Zustand.
 * - `sgb5_medigabe_taeglich`: Schieberegler, steht ohne Wert sichtbar auf 1.
 * - `bk_raucher`: leer bedeutet ausdrücklich „Egal" (Platzhalter).
 * - Sektion 02 Versicherungsdaten (Krankenkasse, Kennnummern wie
 *   Sozialversicherungsnr., LBNR): nie Pflicht, auch wenn die Sektion
 *   aktiv ist (Ticket „rot markierte Felder aus 02 keine Pflichtfelder").
 */
const SECTION_OPTIONAL = new Set<keyof PbFormState>([
  ...(TOGGLE_SECTIONS.versicherung as ReadonlyArray<keyof PbFormState>),
  'sozialamt_aufstockung',
  'ausgepraegtes_familienleben',
  'sgb5_medigabe',
  'sgb5_medigabe_taeglich',
  'sgb5_ats',
  'bk_raucher',
]);

/**
 * Pflichtfelder einer aktiven Sektion: alle sichtbaren Felder müssen
 * komplett ausgefüllt sein (Ticket „SIS bzw. aktivierte Bereiche komplett").
 */
export function sectionRequiredFields(id: ToggleSectionId): Array<keyof PbFormState> {
  return (TOGGLE_SECTIONS[id] as ReadonlyArray<keyof PbFormState>).filter(k => !SECTION_OPTIONAL.has(k));
}

/** In welcher Ansicht wird validiert? */
export interface RequiredContext {
  /** Werden die Sektionen 02–07 angezeigt? Default: `is_pflegeempfaenger`. */
  fullSections?: boolean;
  /** Sektionen, die in dieser Ansicht nicht bearbeitbar sind (Rechte, nur lesend). */
  readOnlySections?: ReadonlyArray<ToggleSectionId>;
}

/**
 * Kontext für FormSections/PbProfileEdit: schlanke Sicht, Schreibrechte und
 * nur lesende Freitexte. Eine Sektion, die man hier nicht ausfüllen kann,
 * darf das Speichern nicht blockieren.
 */
export function formRequiredContext(opts: {
  slim?: boolean; perms?: SectionPerms; freitexteReadOnly?: boolean;
}): RequiredContext {
  const readOnlySections: ToggleSectionId[] = [];
  if (opts.perms && !(opts.perms.versicherung.canRead && opts.perms.versicherung.canWrite)) {
    readOnlySections.push('versicherung');
  }
  if (opts.freitexteReadOnly) readOnlySections.push('freitexte');
  return { fullSections: !opts.slim, readOnlySections };
}

/** Ergebnis-Typ von validateProfile(). */
export interface ValidationResult {
  /** Alle fehlenden Pflichtfeld-Keys. */
  missing: Array<keyof PbFormState>;
  /** Section-id des ersten fehlenden Feldes — für jump(). */
  firstSection: string | null;
}

/** Ordnet jeden Pflichtfeld-Key der Section zu, in der es liegt. */
export const FIELD_SECTION_MAP: Partial<Record<keyof PbFormState, string>> = {
  profiltyp:      'profil',
  vorname:        'stammdaten',
  nachname:       'stammdaten',
  anrede:         'stammdaten',
  familienstand:  'stammdaten',
  land:           'stammdaten',
  telefon:        'stammdaten',
  pflegegrad:     'pflege',
  beweglichkeit:  'pflege',
  geisteszustand: 'pflege',
  schlafverhalten:'pflege',
  grundpflege:    'pflege',
  hygiene:        'pflege',
  ankleiden:      'pflege',
  essen:          'pflege',
  urinkontrolle:  'pflege',
  stuhlkontrolle: 'pflege',
  raucher:        'pflege',
  besserung:      'pflege',
  badge:          'wunsch',
  // Alle Felder der Sektionen 02–07 liegen in der Sektion ihres Schalters.
  ...Object.fromEntries(
    (Object.keys(TOGGLE_SECTIONS) as ToggleSectionId[]).flatMap(id =>
      TOGGLE_SECTIONS[id].map(k => [k, id] as const)),
  ),
};

/** Reihenfolge der Sektionen auf der Seite (für Sprungziel und Meldung). */
const SECTION_ORDER: string[] = ['profil', 'stammdaten', ...Object.keys(TOGGLE_SECTIONS)];

/**
 * Alle Pflichtfeld-Keys für den aktuellen Formular-State.
 *
 * Einzige Quelle für „ist dieses Feld Pflicht?" — Sternchen (`*`) im
 * Formular und die Validierung beim Speichern lesen beide hier, damit die
 * Markierung nicht von der tatsächlichen Prüfung abdriften kann. Die
 * Pflege-Pflichtfelder hängen an `is_pflegeempfaenger`; ein statisches
 * Sternchen an ihnen würde bei Kontaktpersonen lügen.
 */
export function requiredFields(f: PbFormState, ctx: RequiredContext = {}): Array<keyof PbFormState> {
  const out = new Set<keyof PbFormState>(REQUIRED_CONTACT);
  if (f.is_pflegeempfaenger) REQUIRED_PFLEGE_STAMMDATEN.forEach(k => out.add(k));
  const full = ctx.fullSections ?? Boolean(f.is_pflegeempfaenger);
  if (!full) return [...out];
  // Die Pflege-Pflichtfelder liegen alle in Sektion 03 — ist sie per
  // Schalter deaktiviert, gelten sie nicht (s. sectionToggles.ts).
  if (Boolean(f.is_pflegeempfaenger) && isSectionActive(f, 'pflege')) {
    REQUIRED_PFLEGE.forEach(k => out.add(k));
  }
  // Aktivierte Sektionen 02–07 müssen komplett ausgefüllt sein.
  for (const id of Object.keys(TOGGLE_SECTIONS) as ToggleSectionId[]) {
    if (!isSectionActive(f, id) || ctx.readOnlySections?.includes(id)) continue;
    sectionRequiredFields(id).forEach(k => out.add(k));
    if (id === 'wunsch') REQUIRED_WUNSCH.forEach(k => out.add(k));
  }
  return [...out];
}

/** Ist `key` im aktuellen Formular-State ein Pflichtfeld? */
export function isRequiredField(f: PbFormState, key: keyof PbFormState, ctx?: RequiredContext): boolean {
  return requiredFields(f, ctx).includes(key);
}

/**
 * Validiert den Formular-State und gibt alle fehlenden Pflichtfelder zurück.
 * Bei is_pflegeempfaenger=true werden auch REQUIRED_PFLEGE geprüft, dazu
 * alle Felder jeder aktivierten Sektion 02–07 (z. B. SIS).
 */
export function validateProfile(f: PbFormState, ctx?: RequiredContext): ValidationResult {
  const keys = requiredFields(f, ctx);

  // Nur Leerzeichen zählt nicht als ausgefüllt.
  const missing = keys.filter(k => {
    const v = f[k];
    if (v == null || (Array.isArray(v) && v.length === 0)) return true;
    const str = typeof v === 'string' ? v.trim() : typeof v === 'number' ? String(v) : null;
    return str === '' || (str != null && (EMPTY_VALUES[k] ?? []).includes(str));
  });
  // In Seitenreihenfolge — Sprungziel und Meldung beginnen bei der ersten Lücke.
  const rank = (k: keyof PbFormState) => SECTION_ORDER.indexOf(FIELD_SECTION_MAP[k] ?? '');
  missing.sort((a, b) => rank(a) - rank(b));

  const firstSection =
    missing.length > 0 ? (FIELD_SECTION_MAP[missing[0]] ?? null) : null;

  return { missing, firstSection };
}

/**
 * Fehlende Felder als lesbare Feldnamen für die Fehlermeldung — die rohen
 * Keys (`freitext_sis_3`) sagen niemandem etwas.
 */
export function missingFieldLabels(
  missing: ReadonlyArray<keyof PbFormState>,
  tr: (key: string, opts: { defaultValue: string }) => string,
): string {
  return missing.map(k => tr(`pflege.form.field.${String(k)}`, { defaultValue: String(k) })).join(', ');
}
